// Live vol-surface snapshot for a single token (IM-1907). Mirrors the
// market-data "Implied Vol Surface" panel's wire exactly (see
// providers/market-data/hooks/use-vols-data.ts subscribeLive): the
// `subscribe.latest_vol_surface` channel, params { type:'CMVOLSURFACE',
// token, timestamp, model:'SVI' }, with pushes arriving as
// `params.data.tenors` — a Record<tenorLabel, Record<volLabel, number>>
// carrying DF/T/ATMF/Future plus the delta ladder (10P/25P/25C/10C).
//
// Why this rather than use-implied-vol-smile.ts: that hook requests only
// deltas:[0.5], so its per-tenor map carries ATMF but NOT the 25-delta
// wings — it cannot produce a 25RR. latest_vol_surface carries the full
// ladder, exactly as the market-data page shows for auto-generated tokens.
//
// Used by the exotics pricer's locked-token banner to source ATMF/25RR
// tile CURRENT values for non-deribit tokens, which have no stored vol
// timeseries (cm_time_series returns nothing) — so the surface is the only
// live source. No intraday history exists, so the banner renders High/Low
// as "—" for these; this hook deliberately exposes only the current
// snapshot.
//
// SUBSCRIPTION ROUTING (see providers/websocket-context/handle-messages.ts):
// the request id MUST contain SUBSCRIPTIONS_KEY.CMVOL_SUBSCRIPTION so the
// open-channel response registers the channel, after which `method:'update'`
// pushes route by channel_id back to the handler registered here. Cleanup
// calls unsubscribeFromChannel, which sends unsubscribe_channel and drops
// the handler.
import { useContext, useEffect, useRef, useState } from 'react';

import { SUBSCRIPTIONS_KEY } from '../../constants/websocket-events';
import { WebSocketContext } from '../websocket-context/websocket-provider';

import { VolSurfaceTenors } from './use-implied-vol-smile';

export const useLiveVolSurface = (
  token: string,
  enabled: boolean,
): {
  tenors: VolSurfaceTenors | null;
  lastUpdated: number | null;
} => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [tenors, setTenors] = useState<VolSurfaceTenors | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // WS context hands back fresh function refs each render; capture in a ref
  // so the subscribe effect isn't re-fired by identity churn.
  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  useEffect(() => {
    // Reset when the token changes or the source flips to deribit — the
    // previous token's surface must not leak into the new tiles.
    setTenors(null);
    setLastUpdated(null);

    if (!enabled || !token || !isConnected) return;
    const ws = wsRef.current;
    if (!ws.sendMessage || !ws.subscribeToChannel) return;

    // Distinct from the market-data page's own CMVOL_SUBSCRIPTION_<symbol>
    // channels so the two never share an activeChannels key; still contains
    // CMVOL_SUBSCRIPTION so isSubscriptionKey() opens the channel.
    const subId = `${SUBSCRIPTIONS_KEY.CMVOL_SUBSCRIPTION}_exotics_locked_${token}`;

    const handler = (raw: unknown) => {
      const rec = (v: unknown): Record<string, unknown> | undefined =>
        v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
      const data = rec(rec(raw)?.params)?.data;
      const nextTenors = rec(data)?.tenors;
      if (!nextTenors || typeof nextTenors !== 'object') return;
      setTenors(nextTenors as VolSurfaceTenors);
      setLastUpdated(Date.now());
    };

    ws.subscribeToChannel(subId, handler);
    ws.sendMessage(
      JSON.stringify({
        jsonrpc: '2.0',
        id: subId,
        method: 'subscribe.latest_vol_surface',
        params: {
          type: 'CMVOLSURFACE',
          token,
          timestamp: new Date().toISOString(),
          model: 'SVI',
        },
      }),
    );

    return () => {
      const unsub = wsRef.current.unsubscribeFromChannel;
      if (unsub) {
        try {
          unsub(subId);
        } catch {
          /* ignore */
        }
      }
    };
  }, [token, enabled, isConnected]);

  return { tenors, lastUpdated };
};
